import { ProductTypeEnum } from '../types/ProductType';
import type { PaymentPurchasedProductLine } from '../types/Payment';
import {
  ticketAttendanceStatuses,
  type TicketAttendanceStatus,
} from '../types/TicketAttendanceStatus';

export function defaultAttendanceStatuses({
  quantity,
}: {
  quantity: number;
}): TicketAttendanceStatus[] {
  return Array.from({ length: quantity }, () => 'not_checked_in');
}

export function normalizeAttendanceStatuses({
  statuses,
  quantity,
}: {
  statuses: TicketAttendanceStatus[] | undefined;
  quantity: number;
}): TicketAttendanceStatus[] {
  const existing = statuses ?? [];
  if (existing.length === quantity) {
    return existing;
  }
  if (existing.length > quantity) {
    return existing.slice(0, quantity);
  }
  return [
    ...existing,
    ...defaultAttendanceStatuses({ quantity: quantity - existing.length }),
  ];
}

export function isTicketAttendanceStatus(v: unknown): v is TicketAttendanceStatus {
  return (
    typeof v === 'string' &&
    (ticketAttendanceStatuses as readonly string[]).includes(v)
  );
}

export function applyTicketAttendanceToPurchasedProducts({
  purchasedProducts,
  productTypeById,
}: {
  purchasedProducts: PaymentPurchasedProductLine[];
  productTypeById: Map<string, ProductTypeEnum | undefined>;
}): PaymentPurchasedProductLine[] {
  return purchasedProducts.map((line) => {
    if (!line.productId) {
      const { attendanceStatuses: _omit, ...rest } = line;
      return rest;
    }
    const productType = productTypeById.get(String(line.productId));
    if (productType !== ProductTypeEnum.TICKET) {
      const { attendanceStatuses: _omit, ...rest } = line;
      return rest;
    }
    const quantity = typeof line.quantity === "number" && line.quantity > 0 ? line.quantity : 1;
    return {
      ...line,
      attendanceStatuses: normalizeAttendanceStatuses({
        statuses: line.attendanceStatuses,
        quantity,
      }),
    };
  });
}
